import type { RawDraftContentState } from '@ms/yammer-content-state';
import type { Entity } from '@ms/yammer-data-support';
import type {
  PraiseBadge,
  StorylineAnnouncementAudience,
  StorylineAnnouncementNotificationTarget,
} from '@ms/yammer-graphql';
import type {
  BasePublisherEventProperties,
  PublisherCollapsedEventProperties,
  PublisherEntityType,
  PublisherOpenEventProperties,
} from '@ms/yammer-telemetry';

import type { Topic } from '../topics/types';
import type {
  AnnouncementNotificationTargetEntityReference,
  Campaign,
  Group,
  LiveEvent,
  Message,
  TeamsMeeting,
  Thread,
  User,
} from '../types';

import type { PublisherAttachmentReference, PublisherAttachments } from './attachments.types';

export * from './attachments.types';
export type { PublisherEntityType as PublisherType };

export type HomePublisherDefaultGroupId = null;

export type PublisherLevel = 'ThreadStarter' | 'TopLevelReply' | 'SecondLevelReply';

// This type should be considered to be deprecated; do not add any further values to it without
// first checking in with the publisher v-team. Where possible, use existing type guards below, or
// existing selectors - especially those in publisherTypeSelectors - rather than comparing directly
// against these.
//
// Unfortunately this enum is mixing several concerns that should really be separate:
// Where a message is being posted to (group, storyline, PM)
// The content type of the message (regular, poll, praise, question)
// ...and also whether it's a new message or edit, and a threadstarter vs reply.
//
// This will hopefully replaced with something better at some point in future.
//
// Take care dealing with the Edit publisher type, since a single post type is used for editing
// all types of existing messages. It's often necessary to look up the original message to determine
// what the scope/target or content type should be. Many existing selectors already do this two-step
// check - first checking post type directly for new messages, followed by a separate check against
// the original message to handle the edit case.
type PublisherPostType =
  | 'DirectNew' // @deprecated(reason: 'use publisher.destination instead')
  | 'DirectReply' // @deprecated(reason: 'use publisher.isReply and/or publisher.destination instead')
  | 'Edit' // @deprecated(reason: 'use publisher.isEditing instead')
  | 'EventNewUpdate' // @deprecated(reason: 'use publisher.destination instead')
  | 'EventNewQuestion' // @deprecated(reason: 'use publisher.destination instead')
  | 'EventReply' // @deprecated(reason: 'use publisher.destination instead')
  | 'GroupNewUpdate'
  | 'GroupNewPoll'
  | 'GroupNewPraise'
  | 'GroupNewQuestion'
  | 'GroupNewArticle'
  | 'GroupReply'
  | 'TeamsMeetingNewUpdate'
  | 'TeamsMeetingNewQuestion'
  | 'TeamsMeetingReply'
  | 'UserNewUpdate'
  | 'UserNewPoll'
  | 'UserNewPraise'
  | 'UserNewQuestion'
  | 'UserNewArticle'
  | 'UserNewMedia'
  | 'UserReply'
  | 'NetworkQuestionNewQuestion'
  | 'NetworkQuestionReply';

type PublisherDestination = 'Direct' | 'Event' | 'Group' | 'NetworkQuestion' | 'TeamsMeeting' | 'User';

export type PublisherContentType = 'Regular' | 'Poll' | 'Praise' | 'Question' | 'Article';

type QuestionPromptState = 'open' | 'dismissed' | 'clicked';

export type PublisherTelemetry =
  | Omit<PublisherOpenEventProperties, 'type' | 'instanceId'>
  | Omit<PublisherCollapsedEventProperties, 'type' | 'instanceId'>
  | Omit<BasePublisherEventProperties, 'type' | 'instanceId'>;

interface TypeBasePublisher extends Entity {
  readonly isEditing: boolean;
  readonly isReply: boolean;
  readonly destination: PublisherDestination;
  readonly contentType: PublisherContentType;
}

interface BasePublisher extends TypeBasePublisher {
  readonly notifiedUserIds: User['id'][];
  readonly publisherInstanceId: string;
  readonly attachments: PublisherAttachments;
  readonly inlineAttachmentIds: string[];
  readonly hasAttemptedToAttachFile: boolean;
  readonly isGifPickerOpen: boolean;
  readonly isTopicBarOpen: boolean;
  readonly isTopicBarDismissed: boolean;
  readonly postType: PublisherPostType;
  readonly rawContentState?: RawDraftContentState;
  readonly title?: string;
  readonly isExpanded: boolean;
  readonly isAnnouncement: boolean;
  readonly changedGroupWhileFilesAttached: boolean;
  readonly changedPostOnBehalfOfUserWhileFilesAttached: boolean;
  readonly sharedThreadId?: Thread['id'];
  readonly sharedTeamsMeetingId?: TeamsMeeting['id'];
  readonly sharedMessageId?: Message['id'];
  readonly topicNames: Topic['name'][];
  readonly topicIds: Topic['id'][];
  readonly isMandatoryNotification: boolean;
  readonly hasToggledMentionsBarToVisible?: boolean;
  readonly headerImage?: PublisherAttachmentReference;
  readonly questionPromptState?: QuestionPromptState;
  readonly campaignReferences: string[];
  readonly isBlockedByModeration?: boolean;
}

export interface BaseReplyPublisher extends BasePublisher {
  readonly currentlyReplyingToId?: Message['id'];
  readonly isSecondLevelReply: boolean;
  readonly threadId: Thread['id'];
  readonly onBehalfOfSenderId?: User['id'];
  readonly isAnnouncement: false;
  readonly isTopicBarOpen: false;
}

export interface ThreadStarterPublisher extends BasePublisher {
  readonly badge: PraiseBadge;
  readonly pollOptions: string[];
  readonly praisedUserIds: User['id'][];
}

export interface EventPublisher extends BasePublisher {
  readonly eventId: LiveEvent['id'];
  readonly groupId: Group['id'];
  readonly onBehalfOfSenderId?: User['id'];
  readonly postType: 'EventNewUpdate' | 'EventNewQuestion' | 'EventReply';
  readonly destination: 'Event';
  readonly contentType: 'Regular' | 'Question';
  readonly isEditing: false;
}

export interface TeamsMeetingThreadStarterPublisher extends ThreadStarterPublisher {
  readonly teamsMeetingId: TeamsMeeting['id'];
  readonly postType: 'TeamsMeetingNewUpdate' | 'TeamsMeetingNewQuestion';
  readonly destination: 'TeamsMeeting';
  readonly isEditing: false;
  readonly isReply: false;
  readonly isAnonymousMessage: boolean;
  readonly isModeratorMessage: boolean;
  readonly onBehalfOfSenderId?: User['id'];
}

export interface TeamsMeetingNewQuestionPublisher extends TeamsMeetingThreadStarterPublisher {
  readonly postType: 'TeamsMeetingNewQuestion';
  readonly destination: 'TeamsMeeting';
  readonly contentType: 'Question';
  readonly isEditing: false;
  readonly isReply: false;
}

export interface TeamsMeetingNewUpdatePublisher extends TeamsMeetingThreadStarterPublisher {
  readonly postType: 'TeamsMeetingNewUpdate';
  readonly destination: 'TeamsMeeting';
  readonly contentType: 'Regular';
  readonly isEditing: false;
  readonly isReply: false;
}

export interface TeamsMeetingReplyPublisher extends BaseReplyPublisher {
  readonly postType: 'TeamsMeetingReply';
  readonly destination: 'TeamsMeeting';
  readonly contentType: 'Regular';
  readonly isEditing: false;
  readonly isReply: true;
  readonly isBestReply: boolean;
  readonly isModeratorMessage: boolean;
  readonly isAnonymousMessage: boolean;
}

export interface GroupThreadStarterPublisher extends ThreadStarterPublisher {
  readonly groupId: Group['id'] | HomePublisherDefaultGroupId;
  readonly campaignId?: Campaign['id'];
  readonly onBehalfOfSenderId?: User['id'];
  readonly postType: 'GroupNewUpdate' | 'GroupNewPoll' | 'GroupNewPraise' | 'GroupNewQuestion' | 'GroupNewArticle';
  readonly destination: 'Group';
  readonly isEditing: false;
  readonly isReply: false;
  readonly topicId?: Topic['id'];
  readonly wasPublishedAsDraft: boolean;
  readonly campaignHashtag?: string;
  readonly title?: string;
}

export interface ArticleThreadStarterPublisher extends ThreadStarterPublisher {
  readonly articleId?: Group['id'] | User['id'];
  readonly onBehalfOfSenderId?: User['id'];
}

export interface GroupNewPollPublisher extends GroupThreadStarterPublisher {
  readonly postType: 'GroupNewPoll';
  readonly contentType: 'Poll';
  readonly destination: 'Group';
  readonly isEditing: false;
  readonly isReply: false;
}

export interface GroupNewPraisePublisher extends GroupThreadStarterPublisher {
  readonly postType: 'GroupNewPraise';
  readonly contentType: 'Praise';
  readonly destination: 'Group';
  readonly isEditing: false;
  readonly isReply: false;
}

export interface GroupNewQuestionPublisher extends GroupThreadStarterPublisher {
  readonly postType: 'GroupNewQuestion';
  readonly contentType: 'Question';
  readonly destination: 'Group';
  readonly isEditing: false;
  readonly isReply: false;
  readonly hasCollapsedRelatedQuestionsModule?: boolean;
}

export interface GroupNewArticlePublisher extends GroupThreadStarterPublisher {
  readonly postType: 'GroupNewArticle';
  readonly contentType: 'Article';
  readonly destination: 'Group';
  readonly isEditing: false;
  readonly isReply: false;
}

export interface GroupNewUpdatePublisher extends GroupThreadStarterPublisher {
  readonly postType: 'GroupNewUpdate';
  readonly contentType: 'Regular';
  readonly destination: 'Group';
  readonly isEditing: false;
  readonly isReply: false;
}

export interface UserThreadStarterPublisher extends ThreadStarterPublisher {
  readonly userId: User['id'];
  readonly campaignId?: Campaign['id'];
  readonly onBehalfOfSenderId?: User['id'];
  readonly postType:
    | 'UserNewUpdate'
    | 'UserNewPoll'
    | 'UserNewPraise'
    | 'UserNewQuestion'
    | 'UserNewArticle'
    | 'UserNewMedia';
  readonly destination: 'User';
  readonly isEditing: false;
  readonly isReply: false;
  readonly topicId?: Topic['id'];
  readonly campaignHashtag?: string;
  readonly announcementAudience?: StorylineAnnouncementAudience;
  readonly announcementAudienceIds?: string[];
  readonly announcementTemporaryAudienceIds?: string[];
  readonly announcementNotificationTargets?: StorylineAnnouncementNotificationTarget[];
  readonly wasPublishedAsDraft: boolean;
}

export interface UserNewPollPublisher extends UserThreadStarterPublisher {
  readonly postType: 'UserNewPoll';
  readonly destination: 'User';
  readonly contentType: 'Poll';
  readonly isEditing: false;
  readonly isReply: false;
}

export interface UserNewMediaPublisher extends UserThreadStarterPublisher {
  readonly postType: 'UserNewMedia';
  readonly destination: 'User';
  readonly contentType: 'Regular';
  readonly isEditing: false;
  readonly isReply: false;
}

export interface UserNewPraisePublisher extends UserThreadStarterPublisher {
  readonly postType: 'UserNewPraise';
  readonly destination: 'User';
  readonly contentType: 'Praise';
  readonly isEditing: false;
  readonly isReply: false;
}

export interface UserNewQuestionPublisher extends UserThreadStarterPublisher {
  readonly postType: 'UserNewQuestion';
  readonly destination: 'User';
  readonly isEditing: false;
  readonly isReply: false;
}

export interface UserNewArticlePublisher extends UserThreadStarterPublisher {
  readonly postType: 'UserNewArticle';
  readonly destination: 'User';
  readonly contentType: 'Article';
  readonly isEditing: false;
  readonly isReply: false;
}

export interface UserNewUpdatePublisher extends UserThreadStarterPublisher {
  readonly postType: 'UserNewUpdate';
  readonly destination: 'User';
  readonly contentType: 'Regular';
  readonly isEditing: false;
  readonly isReply: false;
}

export interface GroupReplyPublisher extends BaseReplyPublisher {
  readonly postType: 'GroupReply';
  readonly destination: 'Group';
  readonly contentType: 'Regular';
  readonly isEditing: false;
  readonly isReply: true;
  readonly groupId: Group['id'];
  readonly agentProposalItemId?: string;
}

export interface UserReplyPublisher extends BaseReplyPublisher {
  readonly postType: 'UserReply';
  readonly destination: 'User';
  readonly contentType: 'Regular';
  readonly isEditing: false;
  readonly isReply: true;
  readonly userId: User['id'];
  readonly agentProposalItemId?: string;
}

export interface DirectPublisher extends BasePublisher {
  readonly postType: 'DirectNew' | 'DirectReply';
  readonly destination: 'Direct';
  readonly contentType: 'Regular';
  readonly isEditing: false;
  readonly isAnnouncement: false;
  readonly isTopicBarOpen: false;
  readonly sharedMessageId?: Message['id'];
}

export interface EditMessagePublisher extends BasePublisher {
  readonly postType: 'Edit';
  readonly destination: 'Group';
  readonly contentType: 'Regular';
  readonly isEditing: true;
  readonly isReply: false;
  readonly messageId: Message['id'];
  readonly originalNotifiedUserIds: User['id'][];
  readonly praisedUserIds: User['id'][];
  readonly badge?: PraiseBadge;
  readonly isTopicBarOpen: false;
  readonly hasRemovedAttachmentsNotDeletedFromSharePoint: boolean;
  readonly onBehalfOfSenderId?: User['id'];
  readonly shouldShowTitle?: boolean;
  readonly isConvertingToQuestion?: boolean;
  readonly removeHeaderImage?: boolean;
  readonly announcementAudience?: StorylineAnnouncementAudience;
  readonly announcementAudienceIds?: string[];
  readonly announcementTemporaryAudienceIds?: string[];
  readonly announcementNotificationTargets?: StorylineAnnouncementNotificationTarget[];
  readonly announcementNotificationTargetEntityReferences?: AnnouncementNotificationTargetEntityReference[];
  readonly pollOptions?: string[];
}

export interface NetworkQuestionNewQuestionPublisher extends ThreadStarterPublisher {
  readonly postType: 'NetworkQuestionNewQuestion';
  readonly destination: 'NetworkQuestion';
  readonly contentType: 'Question';
  readonly isEditing: false;
  readonly isReply: false;
  readonly isTopicBarOpen: true;
  readonly hasCollapsedRelatedQuestionsModule: boolean;
}

export interface NetworkQuestionReplyPublisher extends BaseReplyPublisher {
  readonly postType: 'NetworkQuestionReply';
  readonly destination: 'NetworkQuestion';
  readonly contentType: 'Regular';
  readonly isEditing: false;
  readonly isReply: true;
}

export type PollPublisher = GroupNewPollPublisher | UserNewPollPublisher;
export type PraisePublisher = GroupNewPraisePublisher | UserNewPraisePublisher;
export type QuestionPublisher =
  | EventPublisher
  | GroupNewQuestionPublisher
  | UserNewQuestionPublisher
  | NetworkQuestionNewQuestionPublisher;
export type ArticlePublisher = GroupNewArticlePublisher | UserNewArticlePublisher;

export type GroupPublisher = GroupReplyPublisher | GroupThreadStarterPublisher;
export type UserPublisher = UserReplyPublisher | UserThreadStarterPublisher;
export type TeamsMeetingPublisher = TeamsMeetingReplyPublisher | TeamsMeetingThreadStarterPublisher;
export type NetworkQuestionPublisher = NetworkQuestionNewQuestionPublisher | NetworkQuestionReplyPublisher;

export type Publisher =
  | BaseReplyPublisher
  | GroupPublisher
  | EventPublisher
  | DirectPublisher
  | TeamsMeetingPublisher
  | EditMessagePublisher
  | UserPublisher
  | NetworkQuestionPublisher;

export const isPollPublisher = (publisher: Publisher): publisher is PollPublisher =>
  publisher.postType === 'GroupNewPoll' || publisher.postType === 'UserNewPoll';

export const isArticlePublisher = (publisher: Publisher): publisher is ArticlePublisher =>
  publisher.postType === 'GroupNewArticle' || publisher.postType === 'UserNewArticle';

export const isPraisePublisher = (publisher: Publisher): publisher is PraisePublisher =>
  publisher.postType === 'GroupNewPraise' || publisher.postType === 'UserNewPraise';

export const isQuestionPublisher = (publisher: Publisher): publisher is QuestionPublisher =>
  publisher.postType === 'EventNewQuestion' ||
  publisher.postType === 'UserNewQuestion' ||
  publisher.postType === 'GroupNewQuestion' ||
  publisher.postType === 'NetworkQuestionNewQuestion';

export const isDirectPublisher = (publisher: Publisher): publisher is DirectPublisher =>
  publisher.destination === 'Direct';

export const isEventReplyPublisher = (publisher: Publisher): publisher is EventPublisher & BaseReplyPublisher =>
  publisher.postType === 'EventReply';

export const isEventThreadStarterPublisher = (publisher: Publisher): publisher is EventPublisher =>
  publisher.postType === 'EventNewQuestion' || publisher.postType === 'EventNewUpdate';

export const isEventPublisher = (publisher: Publisher): publisher is EventPublisher =>
  isEventThreadStarterPublisher(publisher) || isEventReplyPublisher(publisher);

export const isGroupPollPublisher = (publisher: Publisher): publisher is GroupNewPollPublisher =>
  publisher.postType === 'GroupNewPoll';

export const isGroupPraisePublisher = (publisher: Publisher): publisher is GroupNewPraisePublisher =>
  publisher.postType === 'GroupNewPraise';

export const isGroupQuestionPublisher = (publisher: Publisher): publisher is GroupNewQuestionPublisher =>
  publisher.postType === 'GroupNewQuestion';

export const isGroupReplyPublisher = (publisher: Publisher): publisher is GroupReplyPublisher =>
  publisher.postType === 'GroupReply';

export const isGroupPublisher = (publisher: Publisher): publisher is GroupPublisher =>
  isGroupThreadStarterPublisher(publisher) || isGroupReplyPublisher(publisher);

export const isGroupThreadStarterPublisher = (publisher: Publisher): publisher is GroupThreadStarterPublisher =>
  publisher.postType === 'GroupNewPoll' ||
  publisher.postType === 'GroupNewPraise' ||
  publisher.postType === 'GroupNewQuestion' ||
  publisher.postType === 'GroupNewArticle' ||
  publisher.postType === 'GroupNewUpdate';

export const isTeamsMeetingReplyPublisher = (publisher: Publisher): publisher is TeamsMeetingReplyPublisher =>
  publisher.postType === 'TeamsMeetingReply';

export const isTeamsMeetingPublisher = (publisher: Publisher): publisher is TeamsMeetingPublisher =>
  isTeamsMeetingThreadStarterPublisher(publisher) || isTeamsMeetingReplyPublisher(publisher);

export const isTeamsMeetingThreadStarterPublisher = (
  publisher: Publisher
): publisher is TeamsMeetingThreadStarterPublisher =>
  publisher.postType === 'TeamsMeetingNewQuestion' || publisher.postType === 'TeamsMeetingNewUpdate';

export const isShareTeamsMeetingPublisher = (publisher: Publisher): publisher is Publisher =>
  publisher.sharedTeamsMeetingId != null;

export const isUserThreadStarterPublisher = (publisher: Publisher): publisher is UserThreadStarterPublisher =>
  publisher.postType === 'UserNewPoll' ||
  publisher.postType === 'UserNewPraise' ||
  publisher.postType === 'UserNewQuestion' ||
  publisher.postType === 'UserNewArticle' ||
  publisher.postType === 'UserNewMedia' ||
  publisher.postType === 'UserNewUpdate';

export const isUserPollPublisher = (publisher: Publisher): publisher is UserNewPollPublisher =>
  publisher.postType === 'UserNewPoll';

export const isUserPraisePublisher = (publisher: Publisher): publisher is UserNewPraisePublisher =>
  publisher.postType === 'UserNewPraise';

export const isUserQuestionPublisher = (publisher: Publisher): publisher is UserNewQuestionPublisher =>
  publisher.postType === 'UserNewQuestion';

export const isUserReplyPublisher = (publisher: Publisher): publisher is UserReplyPublisher =>
  publisher.postType === 'UserReply';

export const isUserPublisher = (publisher: Publisher): publisher is UserPublisher =>
  isUserThreadStarterPublisher(publisher) || isUserReplyPublisher(publisher);

export const isNetworkQuestionNewQuestionPublisher = (
  publisher: Publisher
): publisher is NetworkQuestionNewQuestionPublisher => publisher.postType === 'NetworkQuestionNewQuestion';

export const isNetworkQuestionReplyPublisher = (publisher: Publisher): publisher is NetworkQuestionReplyPublisher =>
  publisher.postType === 'NetworkQuestionReply';

export const isNetworkQuestionPublisher = (publisher: Publisher): publisher is NetworkQuestionPublisher =>
  isNetworkQuestionNewQuestionPublisher(publisher) || isNetworkQuestionReplyPublisher(publisher);

export type ReplyPublisher =
  | BaseReplyPublisher
  | GroupReplyPublisher
  | NetworkQuestionReplyPublisher
  | TeamsMeetingReplyPublisher
  | UserReplyPublisher;
export const isReplyPublisher = (publisher: Publisher): publisher is ReplyPublisher =>
  isGroupReplyPublisher(publisher) ||
  isUserReplyPublisher(publisher) ||
  isNetworkQuestionReplyPublisher(publisher) ||
  isTeamsMeetingReplyPublisher(publisher) ||
  publisher.isReply;

export const isEditPublisher = (publisher: Publisher): publisher is EditMessagePublisher => publisher.isEditing;

export type Publishers = Record<Publisher['id'], Publisher>;

export type MentionsBarState = 'Hidden' | 'ToggledVisible' | 'AlwaysVisible';
